import { computed } from 'vue'
import { BUTTON_CLASSES, ICON_SIZES } from '../constants/button-constants.js'

/**
 * Button styling logic composable
 * @param {Object} props - Component props
 * @param {Object} buttonState - State from useButton composable
 * @returns {Object} CSS classes and styling utilities
 */
export function useButtonStyles(props, buttonState) {
  /**
   * Main button CSS classes
   * @type {import('vue').ComputedRef<string>}
   */
  const buttonClasses = computed(() => {
    const classes = [BUTTON_CLASSES.BASE]
    
    // Add button type class
    classes.push(buttonState.effectiveType.value)
    
    // Add icon-only class if no text
    if (!buttonState.hasText.value) {
      classes.push(BUTTON_CLASSES.ONLY_ICON)
    }
    
    // Add radius class
    if (props.radius) {
      classes.push(BUTTON_CLASSES.RADIUS)
    }
    
    // Add no border class - use effectiveHasBorder from buttonState
    if (!buttonState.effectiveHasBorder.value) {
      classes.push(BUTTON_CLASSES.NO_BORDER)
    }
    
    // Add disabled class
    if (buttonState.isDisabled.value) {
      classes.push(BUTTON_CLASSES.DISABLED)
    }
    
    return classes.join(' ')
  })

  /**
   * Left icon CSS classes
   * @type {import('vue').ComputedRef<string>}
   */
  const leftIconClasses = computed(() => {
    if (!buttonState.hasLeftIcon.value) return ''
    
    const classes = [
      buttonState.iconSizeClass.value,
      'icon',
      'left',
      props.leftIcon
    ]
    
    if (buttonState.isDisabled.value) {
      classes.push(BUTTON_CLASSES.DISABLED)
    }
    
    return classes.join(' ')
  })

  /**
   * Right icon CSS classes
   * @type {import('vue').ComputedRef<string>}
   */
  const rightIconClasses = computed(() => {
    if (!buttonState.hasRightIcon.value) return ''
    
    const classes = [
      ICON_SIZES.SMALL, // Right icon is always small
      'icon',
      'right',
      'margin-left-4',
      props.rightIcon
    ]
    
    if (buttonState.isDisabled.value) {
      classes.push(BUTTON_CLASSES.DISABLED)
    }
    
    return classes.join(' ')
  })

  /**
   * Text container CSS classes
   * @type {import('vue').ComputedRef<string>}
   */
  const textClasses = computed(() => {
    if (!buttonState.hasText.value) return ''
    
    const classes = ['text']
    
    // Add padding classes based on icons
    if (buttonState.hasLeftIcon.value) {
      classes.push('pl-0')
    }
    
    if (buttonState.hasRightIcon.value) {
      classes.push('pr-0')
    }
    
    return classes.join(' ')
  })

  /**
   * Slot container CSS classes
   * @type {import('vue').ComputedRef<string>}
   */
  const slotClasses = computed(() => {
    return 'ms-button-text ms-button--text flex align-center'
  })

  /**
   * Check if left icon should be displayed
   * @type {import('vue').ComputedRef<boolean>}
   */
  const showLeftIcon = computed(() => {
    return buttonState.hasLeftIcon.value
  })

  /**
   * Check if right icon should be displayed
   * @type {import('vue').ComputedRef<boolean>}
   */
  const showRightIcon = computed(() => {
    return buttonState.hasRightIcon.value
  })

  /**
   * Check if text should be displayed
   * @type {import('vue').ComputedRef<boolean>}
   */
  const showText = computed(() => {
    return buttonState.hasText.value
  })

  return {
    // CSS Classes
    buttonClasses,
    leftIconClasses,
    rightIconClasses,
    textClasses,
    slotClasses,
    
    // Visibility
    showLeftIcon,
    showRightIcon,
    showText
  }
}
